import { CharacterMetadata, ContentBlock, ContentState, EditorState, genKey, Modifier } from "draft-js";
import { List, Repeat } from "immutable";
import { AiSuggestionBlockName } from "./types";

export const getSelectedText = (editorState: EditorState): string => {
    const selection = editorState.getSelection();

    if (selection.isCollapsed()) {
        return "";
    }

    const content = editorState.getCurrentContent();
    const endBlockKey = content.getBlockAfter(selection.getEndKey())?.getKey();

    return content
        .getBlockMap()
        .toSeq()
        .skipUntil(block => block.getKey() === selection.getStartKey())
        .takeUntil(block => block.getKey() === endBlockKey)
        .map(block => {
            const start = block.getKey() === selection.getStartKey() ? selection.getStartOffset() : 0;
            const end = block.getKey() === selection.getEndKey() ? selection.getEndOffset() : block.getLength();
            return block.getText().slice(start, end);
        })
        .join("\n");
};

export const getTextWithoutSuggestion = (editorState: EditorState): string => {
    const content = editorState.getCurrentContent();

    return content
        .getBlockMap()
        .toSeq()
        .filter(block => block.getType() !== AiSuggestionBlockName)
        .map(block => block.getText())
        .join("\n");
};

const getSuggestionBlock = (state: EditorState) =>
    state
        .getCurrentContent()
        .getBlockMap()
        .find(x => x.getType() === AiSuggestionBlockName);

/**
 *  Adds AI suggestion to the editor as preview.
 *  User then might apply or discard it.
 */
export const addSuggestion = (state: EditorState, suggestion: string) => {
    const suggestionBlock = getSuggestionBlock(state);
    const suggestionBlockKey = suggestionBlock?.getKey() ?? genKey();

    const newBlock = new ContentBlock({
        key: suggestionBlockKey,
        type: AiSuggestionBlockName,
        text: suggestion,
        characterList: List(Repeat(new CharacterMetadata(), suggestion.length)),
    });

    const newBlockMap = state.getCurrentContent().getBlockMap().set(suggestionBlockKey, newBlock);
    const newContent = ContentState.createFromBlockArray(newBlockMap.toArray());
    const newState = EditorState.push(state, newContent, "insert-fragment");

    return EditorState.forceSelection(newState, state.getSelection());
};

/**
 *  Removes AI completion preview from editor when user presses discard button.
 */
export const cleanupSuggestion = (prevState: EditorState) => {
    const contentState = prevState.getCurrentContent();
    const newBlockMap = contentState.getBlockMap().butLast();
    const content = ContentState.createFromBlockArray(newBlockMap.toArray());

    const state = EditorState.push(prevState, content, "remove-range");
    return EditorState.forceSelection(state, prevState.getSelection());
};

/**
 * Adds AI suggestion to the end of the message.
 */
export const appendSuggestion = (prevState: EditorState) => {
    const suggestionBlock = getSuggestionBlock(prevState);
    const suggestion = suggestionBlock.getText();

    const newBlock = new ContentBlock({
        key: suggestionBlock.getKey(),
        type: "unstyled",
        text: suggestion,
        characterList: List(Repeat(new CharacterMetadata(), suggestion.length)),
    });

    const newBlockMap = prevState.getCurrentContent().getBlockMap().set(suggestionBlock.getKey(), newBlock);

    const content = ContentState.createFromBlockArray(newBlockMap.toArray());
    const state = EditorState.push(prevState, content, "remove-range");

    return EditorState.forceSelection(state, content.getSelectionAfter());
};

/**
 * Replace the message (or the selected text) with the AI suggestion
 */
export const replaceSuggestion = (state: EditorState) => {
    const currentSelection = state.getSelection();
    const suggestion = getSuggestionBlock(state).getText();
    const stateWithoutSuggestion = cleanupSuggestion(state);

    const contentState = stateWithoutSuggestion.getCurrentContent();
    let newContentState: ContentState;

    if (currentSelection.isCollapsed()) {
        newContentState = ContentState.createFromText(suggestion);
    } else {
        newContentState = Modifier.replaceText(contentState, currentSelection, suggestion);
    }
    const newEditorState = EditorState.push(stateWithoutSuggestion, newContentState, "remove-range");

    return EditorState.forceSelection(newEditorState, newContentState.getSelectionAfter());
};

export const isFocusInTheEndOfTheSourceText = (state: EditorState) => {
    let selection = state.getSelection();
    const content = state.getCurrentContent();

    const suggestionBlock = getSuggestionBlock(state);
    if (!suggestionBlock) {
        return false;
    }

    const blockBeforeSuggestion = content.getBlockBefore(suggestionBlock.getKey());
    const blockBeforeSuggestionKey = blockBeforeSuggestion.getKey();

    if (
        selection.getFocusOffset() === blockBeforeSuggestion.getLength() && // if focus placed in the end of the block before suggestion
        selection.getFocusKey() === blockBeforeSuggestionKey // if focus placed in the block before suggestion
    ) {
        return true;
    }

    return false;
};
